import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Input, Field, Loader, Select } from '@itds/elements/index.js';

import { AgencyContext } from '@brokerage/contexts/AgencyContext';

const getDataSource = aq => {
  if ('is_live' in aq && aq.is_live) return 'Live';
  if ('is_beta' in aq && aq.is_beta) return 'Beta';
  return '';
};

const getRateLimitValues = aq => ({
  rate_limit_replenishment_period_seconds:
    'rate_limit_replenishment_period_seconds' in aq ? aq.rate_limit_replenishment_period_seconds : '',
  rate_limit_token_limit: 'rate_limit_token_limit' in aq ? aq.rate_limit_token_limit : '',
  rate_limit_tokens_per_period: 'rate_limit_tokens_per_period' in aq ? aq.rate_limit_tokens_per_period : ''
});

const getMonthlyUsageValues = aq => ({
  monthly_request_limit: 'monthly_request_limit' in aq ? aq.monthly_request_limit : ''
});

const getSolveTypeValues = aq => {
  if (Array.isArray(aq['solve_type']) && aq['solve_type'].length > 0) {
    return aq['solve_type'];
  }

  return [1];
};

const General = ({ isUQDeactivated = false, annuity_quoter = {}, handleSave, token }) => {
  const aq = annuity_quoter || {};
  const { member_names } = useContext(AgencyContext);
  const [data, setData] = useState({
    is_deactivated: Boolean(isUQDeactivated || ('is_deactivated' in aq ? aq.is_deactivated : false)),
    data_source: getDataSource(aq),
    name: String('name' in aq ? aq.name : ''),
    member_id: 'member_id' in aq ? aq.member_id : null,
    required_agent_info: Array.isArray(aq['required_agent_info']) ? aq['required_agent_info'] : [],
    notes: String('notes' in aq ? aq.notes : ''),
    is_archived: Boolean(isUQDeactivated || ('is_archived' in aq ? aq.is_archived : false)),
    rate_limit_enabled: 'rate_limit_enabled' in aq ? aq.rate_limit_enabled : false,
    ...getRateLimitValues(aq),
    monthly_limit_enabled: 'monthly_limit_enabled' in aq ? aq.monthly_limit_enabled : false,
    ...getMonthlyUsageValues(aq),
    // Default Settings - Solve Type (multi-select array with default)
    solve_type: getSolveTypeValues(aq)
  });
  const [errors, setErrors] = useState([]);

  const handleArchiveToggle = () => {
    setData({ ...data, is_archived: !data.is_archived });
    handleSave({ is_archived: !data.is_archived });
  };

  const handleAgentInfoToggle = (name, e) => {
    let required_agent_info = [];

    if (Array.isArray(data.required_agent_info)) {
      required_agent_info = data.required_agent_info.filter(n => n !== name);
    }

    if (e.target.checked) {
      required_agent_info.push(name);
    }
    setData({ ...data, required_agent_info });
  };

  const handleChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };
  const handleCheck = (name, e) => {
    const checked = e.target.checked;

    if (name === 'rate_limit_enabled' && !checked) {
      setData({
        ...data,
        rate_limit_enabled: checked,
        ...getRateLimitValues(aq)
      });

      return;
    }

    if (name === 'monthly_limit_enabled' && !checked) {
      setData({
        ...data,
        monthly_limit_enabled: checked,
        ...getMonthlyUsageValues(aq)
      });

      return;
    }

    setData({ ...data, [name]: checked });
  };

  const rateLimitsReadOnly = !data.rate_limit_enabled || data?.is_deactivated || data?.is_archived;
  const monthlyUsageReadOnly =
    !data.monthly_limit_enabled || data?.is_deactivated || data?.is_archived;
  const sectionHeaderStyle = {
    color: '#666',
    fontSize: 13,
    fontWeight: 600,
    margin: '0 0 12px'
  };

  // Handle multi-select solve type with at least one required
  const handleSolveTypeChange = (solveTypeValue, e) => {
    const isChecked = e.target.checked;
    let newSolveTypes = Array.isArray(data.solve_type) ? [...data.solve_type] : [1];

    if (isChecked) {
      // Add the solve type if not already present
      if (!newSolveTypes.includes(solveTypeValue)) {
        newSolveTypes.push(solveTypeValue);
      }
    } else if (newSolveTypes.length > 1) {
      // Remove the solve type only if at least one other is selected
      newSolveTypes = newSolveTypes.filter(type => type !== solveTypeValue);
    } else {
      // If trying to uncheck the last one, prevent it by not updating state
      return;
    }

    setData({ ...data, solve_type: newSolveTypes });
  };

  const save = () => {
    const errors = [];

    if (!data.name) {
      errors.push('name');
    }
    // Validate that at least one solve type is selected
    if (!Array.isArray(data.solve_type) || data.solve_type.length === 0) {
      errors.push('solve_type');
    }
    setErrors(errors);
    if (!errors.length) {
      const requestData = {
        ...data,
        is_beta: data.data_source === 'Beta',
        is_live: data.data_source === 'Live'
      };

      handleSave(requestData);
    }
  };

  return data ? (
    <>
      {' '}
      <Field label='Token' style={{}}>
        <Input readOnly={true} value={token || aq['token'] || ''} />
      </Field>
      <Field label='' style={{}}>
        <Checkbox
          checked={data.is_deactivated}
          disabled={data.is_archived}
          label='Deactivated'
          onChange={handleCheck.bind(null, 'is_deactivated')}
        />
      </Field>
      <Field label='Name' style={{}}>
        <Input
          invalid={errors.includes('name')}
          onChange={handleChange.bind(null, 'name')}
          readOnly={data?.is_deactivated || data?.is_archived}
          required={true}
          value={data.name || ''}
        />
      </Field>
      <Field label='Member' style={{}}>
        <Select
          onChange={handleChange.bind(null, 'member_id')}
          options={member_names}
          placeholder='-- Select Member --'
          readOnly={data?.is_deactivated || data?.is_archived}
          style={{}}
          value={data.member_id || ''}
        />
      </Field>
      <Field label='Data Source' style={{}}>
        <Select
          onChange={handleChange.bind(null, 'data_source')}
          options={[{ value: 'Live' }, { value: 'Beta' }]}
          placeholder='-- Select Data Source --'
          readOnly={data?.is_deactivated || data?.is_archived}
          style={{}}
          value={data.data_source}
        />
      </Field>{' '}
      <Field
        description="Select the Agent Information that is required to run a quote If you don't want any information to be required, then don't check any of the boxes."
        label='Required Agent Information'
        style={{}}
      >
        <Checkbox
          checked={
            Array.isArray(data.required_agent_info) && data.required_agent_info.includes('email')
          }
          disabled={data?.is_deactivated || data?.is_archived}
          label='Email'
          onChange={handleAgentInfoToggle.bind(null, 'email')}
        />
        <Checkbox
          checked={
            Array.isArray(data.required_agent_info) && data.required_agent_info.includes('phone')
          }
          disabled={data?.is_deactivated || data?.is_archived}
          label='Phone'
          onChange={handleAgentInfoToggle.bind(null, 'phone')}
        />
        <Checkbox
          checked={
            Array.isArray(data.required_agent_info) &&
            data.required_agent_info.includes('first_name')
          }
          disabled={data?.is_deactivated || data?.is_archived}
          label='First Name'
          onChange={handleAgentInfoToggle.bind(null, 'first_name')}
        />
        <Checkbox
          checked={
            Array.isArray(data.required_agent_info) &&
            data.required_agent_info.includes('middle_name')
          }
          disabled={data?.is_deactivated || data?.is_archived}
          label='Middle Name'
          onChange={handleAgentInfoToggle.bind(null, 'middle_name')}
        />
        <Checkbox
          checked={
            Array.isArray(data.required_agent_info) &&
            data.required_agent_info.includes('last_name')
          }
          disabled={data?.is_deactivated || data?.is_archived}
          label='Last Name'
          onChange={handleAgentInfoToggle.bind(null, 'last_name')}
        />
        <Checkbox
          checked={
            Array.isArray(data.required_agent_info) &&
            data.required_agent_info.includes('insurance_license_number')
          }
          disabled={data?.is_deactivated || data?.is_archived}
          label='Insurance License Number'
          onChange={handleAgentInfoToggle.bind(null, 'insurance_license_number')}
        />
      </Field>{' '}
      <Field label='Notes' style={{}}>
        <Input
          height={100}
          onChange={handleChange.bind(null, 'notes')}
          readOnly={data?.is_deactivated || data?.is_archived}
          type='textarea'
          value={data.notes || ''}
        />
      </Field>
      {/* Multi-select Solve Type with validation */}
      <Field
        description='Select one or more solve types for the annuity quoter. At least one must be selected.'
        invalid={errors.includes('solve_type')}
        label='Solve Type'
        required={true}
        style={{ marginTop: 40 }}
      >
        <Checkbox
          checked={Array.isArray(data.solve_type) && data.solve_type.includes(1)}
          disabled={data?.is_deactivated || data?.is_archived}
          label='Solve for Accumulation'
          onChange={handleSolveTypeChange.bind(null, 1)}
        />
        <Checkbox
          checked={Array.isArray(data.solve_type) && data.solve_type.includes(3)}
          disabled={data?.is_deactivated || data?.is_archived}
          label='Solve for Contribution'
          onChange={handleSolveTypeChange.bind(null, 3)}
        />
        <Checkbox
          checked={Array.isArray(data.solve_type) && data.solve_type.includes(2)}
          disabled={data?.is_deactivated || data?.is_archived}
          label='Solve for Rider Income'
          onChange={handleSolveTypeChange.bind(null, 2)}
        />
        <Checkbox
          checked={Array.isArray(data.solve_type) && data.solve_type.includes(4)}
          disabled={data?.is_deactivated || data?.is_archived}
          label='Solve for Annuitization'
          onChange={handleSolveTypeChange.bind(null, 4)}
        />
        {errors.includes('solve_type') && (
          <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '8px' }}>
            At least one solve type must be selected
          </div>
        )}
      </Field>
      <div style={{ marginBottom: 20 }}>
        <div style={sectionHeaderStyle}>Rate Limits</div>
        <Field label=''>
          <Checkbox
            checked={data.rate_limit_enabled}
            disabled={data?.is_deactivated || data?.is_archived}
            label='Enabled'
            onChange={handleCheck.bind(null, 'rate_limit_enabled')}
          />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ width: '49%' }}>
            <Field label='Token Limit'>
              <Input
                onChange={handleChange.bind(null, 'rate_limit_token_limit')}
                readOnly={rateLimitsReadOnly}
                type='number'
                value={data.rate_limit_token_limit || ''}
              />
            </Field>
          </div>
          <div style={{ width: '49%' }}>
            <Field label='Tokens Per Period'>
              <Input
                onChange={handleChange.bind(null, 'rate_limit_tokens_per_period')}
                readOnly={rateLimitsReadOnly}
                type='number'
                value={data.rate_limit_tokens_per_period || ''}
              />
            </Field>
          </div>
        </div>

        <div style={{ width: '49%' }}>
          <Field label='Replenishment Period Seconds'>
            <Input
              onChange={handleChange.bind(null, 'rate_limit_replenishment_period_seconds')}
              readOnly={rateLimitsReadOnly}
              type='number'
              value={data.rate_limit_replenishment_period_seconds || ''}
            />
          </Field>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={sectionHeaderStyle}>Monthly Usage</div>
        <Field label=''>
          <Checkbox
            checked={data.monthly_limit_enabled}
            disabled={data?.is_deactivated || data?.is_archived}
            label='Enabled'
            onChange={handleCheck.bind(null, 'monthly_limit_enabled')}
          />
        </Field>

        <div style={{ width: '49%' }}>
          <Field label='Monthly Request Limit'>
            <Input
              onChange={handleChange.bind(null, 'monthly_request_limit')}
              readOnly={monthlyUsageReadOnly}
              type='number'
              value={data.monthly_request_limit || ''}
            />
          </Field>
        </div>
      </div>
      <div style={{ display: 'flex', marginTop: 40 }}>
        <Button color='RED' onClick={handleArchiveToggle} style={{ marginRight: '5px' }}>
          {data.is_archived ? 'Unarchive' : 'Archive'}
        </Button>
        {data.is_archived ? null : (
          <Button onClick={save} style={{}}>
            Save Changes
          </Button>
        )}
      </div>
    </>
  ) : (
    <Loader />
  );
};

General.propTypes = {
  annuity_quoter: PropTypes.object,
  handleSave: PropTypes.func,
  isUQDeactivated: PropTypes.bool,
  token: PropTypes.string
};

export default General;
